import { useCallback, useEffect, useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import { ContactList } from '../components/ContactList';
import { ContactForm } from '../components/ContactForm';
import { CategoryModal } from '../components/CategoryModal';
import { CSVImportModal } from '../components/CSVImportModal';
import { SearchAndFilters } from '../components/SearchAndFilters';
import { Pagination } from '../components/Pagination';
import { Header } from '../components/Header';
import { Category, Contact } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [isApplyingCategory, setIsApplyingCategory] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  const pageSize = 30;

  const { contacts, total, totalPages, loading, error, refresh, deleteContact } = useContacts({
    search: search || undefined,
    page: currentPage,
    pageSize,
  });

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
    refresh();
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
  };

  const handleNewContact = () => {
    setEditingContact(undefined);
    setIsFormOpen(true);
  };

  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
  };

  const handleOpenCSVImportModal = () => {
    setIsCSVImportModalOpen(true);
  };

  const handleCloseCSVImportModal = () => {
    setIsCSVImportModalOpen(false);
  };

  const handleCSVImportSuccess = () => {
    refresh();
    setIsCSVImportModalOpen(false);
  };

  const handleToggleContactSelection = (id: string) => {
    setSelectedContacts((prev) =>
      prev.includes(id) ? prev.filter((contactId) => contactId !== id) : [...prev, id]
    );
  };

  const handleToggleAllContacts = () => {
    if (contacts.length === 0) {
      return;
    }

    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
      return;
    }

    setSelectedContacts(contacts.map((contact) => contact.id));
  };

  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const data = await apiService.getAllCategories();
      setCategories(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar categorias';
      toast.error(errorMessage);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isCategoryModalOpen) {
      loadCategories();
    }
  }, [isCategoryModalOpen, loadCategories]);

  useEffect(() => {
    setSelectedContacts((prev) =>
      prev.filter((id) => contacts.some((contact) => contact.id === id))
    );
  }, [contacts]);

  const handleApplyCategory = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um contato');
      return;
    }

    if (!bulkCategoryId) {
      toast.error('Selecione uma categoria para aplicar');
      return;
    }

    try {
      setIsApplyingCategory(true);
      const categoriaId = bulkCategoryId === '__clear__' ? null : bulkCategoryId;

      await apiService.updateContactsCategory(selectedContacts, categoriaId);
      toast.success('Categoria atualizada para os contatos selecionados');
      setSelectedContacts([]);
      refresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar categoria';
      toast.error(errorMessage);
    } finally {
      setIsApplyingCategory(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um contato');
      return;
    }

    const confirmed = window.confirm(
      `Tem certeza que deseja excluir ${selectedContacts.length} contato(s)? Esta ação não pode ser desfeita.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setIsDeletingSelected(true);
      const deletions = selectedContacts.map((id) =>
        apiService.deleteContact(id).then(() => id)
      );
      const results = await Promise.allSettled(deletions);
      const failedIds: string[] = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedIds.push(selectedContacts[index]);
        }
      });
      const successCount = selectedContacts.length - failedIds.length;

      if (successCount > 0) {
        toast.success(`${successCount} contato(s) excluído(s) com sucesso`);
      }
      if (failedIds.length > 0) {
        toast.error(`Falha ao excluir ${failedIds.length} contato(s). Tente novamente.`);
      }

      setSelectedContacts(failedIds);
      refresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir contatos';
      toast.error(errorMessage);
    } finally {
      setIsDeletingSelected(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erro</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        title="Contatos"
        subtitle={`${total} contatos cadastrados`}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleOpenCategoryModal}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-medium transition-colors"
              aria-label="Gerenciar categorias"
            >
              Categorias
            </button>
            <button
              onClick={handleOpenCSVImportModal}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
              aria-label="Importar contatos via CSV ou Excel"
            >
              Importar CSV ou Excel
            </button>
            <button
              onClick={handleNewContact}
              className="btn-primary"
              aria-label="Criar novo contato"
            >
              + Novo Contato
            </button>
          </div>
        }
      />

      <div className="p-6 space-y-6">

        <SearchAndFilters
          search={search}
          onSearchChange={handleSearchChange}
          onClearFilters={handleClearFilters}
        />

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center">
              {selectedContacts.length}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Contatos selecionados</p>
              <p className="text-xs text-gray-500">Use as caixas da tabela para selecionar vários contatos.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <select
              value={bulkCategoryId}
              onChange={(e) => setBulkCategoryId(e.target.value)}
              className="w-full sm:w-72 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={categoriesLoading}
            >
              <option value="">Selecione uma categoria</option>
              <option value="__clear__">Remover categoria</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.nome}
                </option>
              ))}
            </select>

            <button
              onClick={handleApplyCategory}
              disabled={isApplyingCategory || isDeletingSelected || !bulkCategoryId || selectedContacts.length === 0}
              className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isApplyingCategory ? 'Aplicando...' : 'Aplicar categoria'}
            </button>

            <button
              onClick={handleBulkDelete}
              disabled={isDeletingSelected || selectedContacts.length === 0}
              className="w-full sm:w-auto px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isDeletingSelected ? 'Excluindo...' : 'Excluir selecionados'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <ContactList
            contacts={contacts}
            loading={loading}
            onEdit={handleEdit}
            onDelete={deleteContact}
            selectedContactIds={selectedContacts}
            onToggleContactSelection={handleToggleContactSelection}
            onToggleAllContacts={handleToggleAllContacts}
          />
        </div>

        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <div className="text-center text-sm text-gray-500">
          Mostrando {contacts.length} de {total} contatos
        </div>
      </div>

      {isFormOpen && (
        <ContactForm
          contact={editingContact}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
      />

      <CSVImportModal
        isOpen={isCSVImportModalOpen}
        onClose={handleCloseCSVImportModal}
        onSuccess={handleCSVImportSuccess}
      />
    </>
  );
}
